// Tour phases, for the browser. Mirrors functions/lib/tourPhases.js, which is the
// authority: the daily rules and the manual step run there. Keep the two in step.
//
// Setup → Booking → Planning → Pre-Tour → Operating → Wrap-Up → Complete → Archived.
// Each step is manual or a count of days/months/years before the start date or after the
// end date; the operator's rules are `settings/tourPhases` (Leadership → Policies).

export const PHASES = Object.freeze(['setup', 'booking', 'planning', 'pre_tour', 'operating', 'wrap_up', 'complete', 'archived'])

export const PHASE_LABELS = Object.freeze({
  setup: 'Setup',
  booking: 'Booking',
  planning: 'Planning',
  pre_tour: 'Pre-Tour',
  operating: 'Operating',
  wrap_up: 'Wrap-Up',
  complete: 'Complete',
  archived: 'Archived',
})

export const RULE_MODES = Object.freeze(['manual', 'before_start', 'after_end'])
export const RULE_UNITS = Object.freeze(['days', 'months', 'years'])

export const DEFAULT_RULES = Object.freeze({
  booking: { mode: 'manual' },
  // Go/No-Go: by hand, through an approval (Aaron, 2026-10-07); due 90 days before the
  // start once approval tasks exist. Until then leadership takes it on the tour's Setup tab.
  planning: { mode: 'manual' },
  pre_tour: { mode: 'before_start', count: 60, unit: 'days' },
  operating: { mode: 'before_start', count: 1, unit: 'days' },
  wrap_up: { mode: 'after_end', count: 1, unit: 'days' },
  complete: { mode: 'manual' },
  archived: { mode: 'after_end', count: 1, unit: 'years' },
})

/**
 * The group chat opens this long before the start date, whatever the phase. Aaron,
 * 2026-09-18: 45 days. The operator's value is `settings/groupChat` (Leadership →
 * Policies → Group chat), which riders cannot read: the daily rules write the resulting
 * date on the tour as `chatOpensOn`, and this default stands in until they have.
 */
export const DEFAULT_CHAT_RULE = Object.freeze({ count: 45, unit: 'days' })

const str = (v) => (typeof v === 'string' ? v.trim() : '')

export function isoDate(value) {
  if (!value) return ''
  if (typeof value === 'object' && typeof value.toDate === 'function') return isoDate(value.toDate())
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10)
  const s = str(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const ms = Date.parse(s)
  return Number.isNaN(ms) ? '' : new Date(ms).toISOString().slice(0, 10)
}

export function todayIso(zone = 'America/Vancouver', nowMs = Date.now()) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(nowMs))
  } catch (e) {
    return new Date(nowMs).toISOString().slice(0, 10)
  }
}

export function shiftDate(iso, count, unit = 'days') {
  const ms = Date.parse(`${iso}T00:00:00Z`)
  if (Number.isNaN(ms)) return ''
  const d = new Date(ms)
  if (unit === 'years') d.setUTCFullYear(d.getUTCFullYear() + count)
  else if (unit === 'months') d.setUTCMonth(d.getUTCMonth() + count)
  else d.setUTCDate(d.getUTCDate() + count)
  return d.toISOString().slice(0, 10)
}

export function tourDates(tour) {
  const schedule = (tour && tour.schedule) || {}
  const complete = schedule.complete || {}
  const primary = schedule.primary || {}
  const start = isoDate(complete.start) || isoDate(primary.start) || isoDate(tour && tour.startDate)
  const end = isoDate(complete.finish) || isoDate(primary.finish) || isoDate(tour && tour.endDate) || start
  return { start, end }
}

export function normalizeRules(doc) {
  const src = (doc && doc.transitions) || doc || {}
  const out = {}
  PHASES.slice(1).forEach((phase) => {
    const d = DEFAULT_RULES[phase]
    const r = src[phase] && typeof src[phase] === 'object' ? src[phase] : {}
    const mode = RULE_MODES.includes(r.mode) ? r.mode : d.mode
    const raw = r.count !== undefined ? r.count : r.days
    const count = Number.isFinite(Number(raw)) && Number(raw) >= 0 ? Math.floor(Number(raw)) : (d.count || 0)
    const unit = RULE_UNITS.includes(r.unit) ? r.unit : (r.count === undefined && r.days !== undefined ? 'days' : (d.unit || 'days'))
    out[phase] = mode === 'manual' ? { mode } : { mode, count, unit }
  })
  return out
}

export function nextPhase(phase) {
  const i = PHASES.indexOf(phase)
  return i >= 0 && i < PHASES.length - 1 ? PHASES[i + 1] : null
}

export function transitionDate(phase, rules, dates) {
  const rule = rules[phase]
  if (!rule || rule.mode === 'manual') return null
  if (rule.mode === 'before_start') return dates.start ? shiftDate(dates.start, -rule.count, rule.unit) : null
  if (rule.mode === 'after_end') return dates.end ? shiftDate(dates.end, rule.count, rule.unit) : null
  return null
}

export function phaseDates(tour, rules) {
  const dates = tourDates(tour)
  const out = {}
  PHASES.slice(1).forEach((phase) => {
    const d = transitionDate(phase, rules, dates)
    if (d) out[phase] = d
  })
  return out
}

export function advanceByRules(from, tour, rules, today) {
  const dates = tourDates(tour)
  const steps = []
  let phase = PHASES.includes(from) ? from : 'setup'
  for (;;) {
    const next = nextPhase(phase)
    if (!next) break
    const on = transitionDate(next, rules, dates)
    if (!on || on > today) break
    steps.push(next)
    phase = next
  }
  return { phase, steps }
}

/**
 * A tour that has never had a phase: cancelled, restricted and draft tours wait in
 * Setup; a tour the old scheduler marked complete or archived keeps that; everything
 * else has been published, so it is taking bookings: Booking, unless its dates have
 * already carried it past Planning. Reads both the old single `status` and the fields of
 * tourState.
 */
export function initialPhase(tour, rules, today) {
  const status = str(tour && tour.status).toLowerCase()
  if (tour && tour.cancelledAt) return 'setup'
  if (status === 'archived') return 'archived'
  if (status === 'complete' || status === 'completed') return 'complete'
  if (['draft', 'cancelled', 'canceled'].includes(status)) return 'setup'
  if (str(tour && tour.visibility).toLowerCase() === 'restricted') return 'setup'
  const walked = advanceByRules('booking', tour, rules, today).phase
  if (walked !== 'booking') return walked
  const dated = advanceByRules('planning', tour, rules, today).phase
  return dated === 'planning' ? 'booking' : dated
}

/** The phase a tour is in right now: what is written, or what it would be given today. */
export function phaseOf(tour, rules = DEFAULT_RULES, today = todayIso()) {
  const written = str(tour && tour.phase)
  if (PHASES.includes(written)) return written
  return initialPhase(tour, normalizeRules(rules), today)
}

/** `settings/groupChat` (or a bare { count, unit }) → { count, unit }, defaults filling the gaps. */
export function normalizeChatRule(doc) {
  const r = doc && doc.opensBefore && typeof doc.opensBefore === 'object' ? doc.opensBefore : (doc || {})
  const count = Number.isFinite(Number(r.count)) && Number(r.count) >= 0 ? Math.floor(Number(r.count)) : DEFAULT_CHAT_RULE.count
  const unit = RULE_UNITS.includes(r.unit) ? r.unit : DEFAULT_CHAT_RULE.unit
  return { count, unit }
}

/** The date the group chat opens for a tour, 'YYYY-MM-DD'; null when the tour has no start. */
export function chatOpensOn(tour, chatRule = DEFAULT_CHAT_RULE) {
  const { start } = tourDates(tour)
  if (!start) return null
  const r = normalizeChatRule(chatRule)
  return shiftDate(start, -r.count, r.unit) || null
}

/** The day the chat opens as the rules wrote it on the tour, else by the default rule. */
export function chatOpensOnFor(tour) {
  const written = str(tour && tour.chatOpensOn)
  if (/^\d{4}-\d{2}-\d{2}$/.test(written)) return written
  return chatOpensOn(tour)
}

/**
 * Whether the group chat exists for a tour today: from `chatOpensOn` until the tour is
 * archived, for a tour that has left Setup and is not cancelled. Not tied to Pre-Tour.
 * Mirrors functions/lib/tourPhases.js chatOpenFor, reading the written date.
 */
export function chatOpen(tour, today = todayIso()) {
  if (tour && tour.cancelledAt) return false
  const phase = phaseOf(tour, DEFAULT_RULES, today)
  if (phase === 'setup' || phase === 'archived') return false
  const on = chatOpensOnFor(tour)
  return Boolean(on) && on <= today
}

/** "45 days before the start date". */
export function describeChatRule(rule) {
  const r = normalizeChatRule(rule)
  const unit = r.count === 1 ? r.unit.replace(/s$/, '') : r.unit
  return `${r.count} ${unit} before the start date`
}

/** "Manual", "90 days before the start date", "1 year after the end date". */
export function describeRule(rule) {
  if (!rule || rule.mode === 'manual') return 'By hand'
  const unit = rule.count === 1 ? rule.unit.replace(/s$/, '') : rule.unit
  return `${rule.count} ${unit} ${rule.mode === 'before_start' ? 'before the start date' : 'after the end date'}`
}
